import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/home-b.css";

/* ============================================================
   Home — "Ann Home B" (Claude Design import)
   Narrative: held breath → the answer → the engine → start
   where you are → what changes → the evidence → two audiences
   → close.
   ============================================================ */

/* The four engine stages, walked by the looping "comet track"
   animation in the engine section — one 16s lap, 4s per stage.
   `readout` is the live status line that cycles top-right; `tag`
   is the artifact each stage hands off to the next. */
const STAGES = [
  {
    n: "01",
    name: "Define Success",
    body:
      "The step the process never numbered — everyone assumed it was already done. " +
      "Ann defines the outcomes that determine success. This becomes RoleDNA.",
    tag: "RoleDNA",
    readout: "defining what success requires",
  },
  {
    n: "02",
    name: "Search",
    body:
      "Ann auto-searches based on role understanding — customised to match your " +
      "company’s talent world — troubleshooting along with you for difficult roles " +
      "— surfacing even hidden talent. Run across hundreds of millions of profiles.",
    tag: "Search rationale",
    readout: "searching your talent world",
  },
  {
    n: "03",
    name: "Engage",
    body:
      "Outreach, follow-up and interview scheduling — the pipeline moves without chasing.",
    tag: "Conversations booked",
    readout: "reaching out, following up",
  },
  {
    n: "04",
    name: "Evaluate",
    body:
      "Résumés read for evidence — strengths credited, gaps admitted, confidence " +
      "stated. Then structured, proctored interviews. One standard across every " +
      "candidate — including the interview itself.",
    tag: "Scored reports",
    readout: "reading the evidence",
  },
];

/* Section 02 — which stages each entry mode lights up. */
const MODES = [
  {
    id: "find",
    label: "Find Talent",
    on: [0, 1, 2],
    desc:
      "Bring a role. Ann finds and engages the people worth evaluating — with the " +
      "reasoning attached.",
  },
  {
    id: "eval",
    label: "Evaluate My Candidates",
    on: [0, 2, 3],
    desc:
      "Already have candidates? Ann evaluates them against a defined standard — " +
      "résumés and structured, proctored interviews.",
  },
  {
    id: "end",
    label: "End-to-End Hiring",
    on: [0, 1, 2, 3],
    desc:
      "The complete engine — from defining success to an evidence-backed shortlist.",
  },
];

/* Section 03 — without Ann (x) / with Ann (c), staged. One stage
   shown at a time via the pill stepper below; auto-advances. */
const STAGE_ROTATE_MS = 2000;
const WC_STAGES = [
  {
    id: "search",
    label: "Search",
    without: [
      "A search that starts from the job title and a keyword guess.",
      "Search quality depends on who ran it, and what their week looked like.",
    ],
    withAnn: [
      "A search built from the definition — customised to your company and its talent universe.",
      "The same designed search every time — run in minutes, not days.",
    ],
  },
  {
    id: "alignment",
    label: "Alignment",
    without: [
      "Stakeholders discover they disagree at the debrief.",
      "Specs change mid-search; the target moves; work repeats.",
    ],
    withAnn: [
      "Stakeholders align on success before anyone is searched.",
      "The definition is agreed upfront — the target holds, rework falls.",
    ],
  },
  {
    id: "engagement",
    label: "Engagement",
    without: ["Outreach is copy-paste, sent when someone finds time."],
    withAnn: [
      "Outreach written to the candidate — sent, followed up, and scheduled without chasing.",
    ],
  },
  {
    id: "evaluation",
    label: "Evaluation",
    without: [
      "The first fifty résumés get read; the rest get skimmed.",
      "Interview quality depends on who was free that day.",
      "Difficult roles get the same search, run harder.",
    ],
    withAnn: [
      "Every candidate evaluated — all of them, against the same standard.",
      "Every candidate gets your best interviewer’s best day — structured, proctored, one standard.",
      "Difficult roles get a diagnosis — market feasibility, adjacent pools, a redesigned search.",
    ],
  },
  {
    id: "decision",
    label: "The decision",
    without: [
      "Shortlists are opinions — decisions die in debate.",
      "Surprises surface at the offer stage.",
      "Weeks between requisition and shortlist.",
    ],
    withAnn: [
      "Shortlists are evidence — the reasoning attached, ready to defend.",
      "Surprises surface at the definition — before they cost you the search.",
      "Minutes to search. Hours to evaluate. Days to a shortlist.",
    ],
  },
];

/* Section 04 — the artifacts Ann actually produces, one real
   role followed end to end. Anonymised. */
const ARTIFACTS = [
  {
    stage: "STAGE 01 · DEFINE SUCCESS",
    tag: "ROLEDNA · BUSINESS DEVELOPMENT SPECIALIST",
    body:
      "CSF 02 — Discovery-led demo that converts interest into a\ncommitted next step.\n\n" +
      "STRONG LOOKS LIKE:\n" +
      "  asks specific workflow questions before sharing a screen,\n" +
      "  tailors the demo path, names what would make the product a\n" +
      "  yes or a no, and closes with a scheduled next step and a\n" +
      "  stakeholder plan.\n\n" +
      "WEAK LOOKS LIKE:\n" +
      "  jumps straight into a feature tour, cannot connect the\n" +
      "  product to the buyer’s process, leaves with “we’ll get back\n" +
      "  to you,” and treats the demo as the finish line.\n\n" +
      "—\n" +
      "Anatomy: Success Milestones · Execution Capabilities ·\nCritical Success Factors · Success Enablers · Hiring Signals",
  },
  {
    stage: "STAGE 02 · SEARCH",
    tag: "SEARCH RATIONALE · ROLE: BUSINESS DEVELOPMENT SPECIALIST",
    body:
      "Search built from the definition:\n" +
      "  Search strings derived from RoleDNA — full-cycle ownership\n" +
      "  signals, outbound meeting creation, HR/TA buyer fluency.\n" +
      "  Target pool expanded beyond SaaS sales titles into\n" +
      "  recruitment agencies and HR-tech adjacent roles.\n\n" +
      "WHY THIS CANDIDATE\n" +
      "  Current title: Senior Recruitment Consultant — an agency\n" +
      "  recruiter, not a “BD Specialist.” Surfaced because the\n" +
      "  evidence maps: has sold services to HR and TA buyers for\n" +
      "  four years, understands hiring pain first-hand, runs a\n" +
      "  personal pipeline end to end.\n\n" +
      "What the title search would have done: never shown this profile.\n" +
      "What the definition shows: persona fluency proven; ramp needed\n" +
      "  on demo structure and proposal cadence — flagged for the\n  interview.",
  },
  {
    stage: "STAGE 04 · EVALUATE",
    tag: "LINKEDIN EVALUATION · AGAINST ROLEDNA",
    body:
      "Profile evidence mapped to the definition — claims located,\n" +
      "strengths credited, open questions carried forward.\n\n" +
      "  ✓ Full-cycle ownership .......... evidenced (3 roles)\n" +
      "  ✓ Buyer fluency (HR/TA) ......... evidenced\n" +
      "  ? Demo structure ................ not visible — carry to interview\n" +
      "  ? Proposal cadence ............. not visible — carry to interview",
  },
  {
    stage: "STAGE 04 · EVALUATE",
    tag: "RÉSUMÉ EVALUATION · AGAINST ROLEDNA",
    body:
      "Claims weighed against the definition.\n\n" +
      "  STRENGTH CREDITED\n" +
      "    “Grew agency desk 0 → £1.2M in 3 yrs”\n" +
      "    source: résumé, corroborated on LinkedIn\n\n" +
      "  GAP ADMITTED\n" +
      "    No evidence of structured SaaS demo delivery.\n" +
      "    Not hidden — flagged, confidence lowered accordingly.",
  },
  {
    stage: "STAGE 03 · ENGAGE",
    tag: "OUTREACH · CANDIDATE-SPECIFIC",
    body:
      "EMAIL\n" +
      "  Subject: A BD role that values agency instincts\n" +
      "  Hi — your desk-building track record maps closely to a\n" +
      "  role I’m working on. Two-minute read attached; open to a\n" +
      "  short call this week?\n\n" +
      "WHATSAPP\n" +
      "  Hi [name] — quick one: a BD role where selling to HR is\n" +
      "  the point, not a hurdle. Worth 10 minutes?",
  },
  {
    stage: "STAGE 04 · EVALUATE",
    tag: "INTERVIEW REPORT · CANDIDATE REPORT",
    body:
      "INTERVIEW INTEGRITY  [ PROCTORED ]\n" +
      "  ✓ Face presence  ✓ Tab monitoring  ✓ AI-usage detection\n" +
      "  Evidence-scored against RoleDNA\n\n" +
      "  85 / 100 suitability  ·  shortlisted · rank 2 of 3\n\n" +
      "  Execution capability ..... 92\n" +
      "    “Shipped work cited from résumé and interview transcript.”\n\n" +
      "  GAP ADMITTED\n" +
      "    No direct evidence of team-lead experience.\n" +
      "    Confidence lowered accordingly.\n\n" +
      "  Report-confidence: High — coverage, depth and consistency\n  verified.",
  },
];

const ANNOS = [
  { label: "Evidence cited", desc: "Every claim links back to its source." },
  { label: "Scores traceable", desc: "Each score decomposes into the signals behind it." },
  { label: "Confidence stated", desc: "Ann says how sure she is, and why." },
  { label: "Gaps admitted", desc: "What is missing is named, never hidden." },
  { label: "Stakeholder ready", desc: "Shareable as-is with hiring managers." },
  { label: "Explainable recommendation", desc: "The reasoning, not just the number." },
];

/* Which single annotation each artifact puts the emphasis on —
   one per slide, in the same order as the annotation list (slide N
   highlights annotation N), so the two lists visibly track together. */
const EMPHASIS = [[0], [1], [2], [3], [4], [5]];

const ROTATE_MS = 7000;
const MODE_ROTATE_MS = 2000;

export default function HomeB() {
  const [mode, setMode] = useState(MODES[0].id);
  const [modePinned, setModePinned] = useState(false); // a chip was clicked — stop rotating
  const [active, setActive] = useState(0);
  const [pinned, setPinned] = useState(false); // a dot was clicked — stop rotating
  const [stage, setStage] = useState(WC_STAGES[0].id);
  const [stagePinned, setStagePinned] = useState(false); // a pill was clicked — stop rotating

  /* Auto-advance the artifact carousel. Reduced motion holds on
     the final artifact instead of cycling. */
  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setActive(ARTIFACTS.length - 1);
      return undefined;
    }
    if (pinned) return undefined;
    const timer = setInterval(
      () => setActive((a) => (a + 1) % ARTIFACTS.length),
      ROTATE_MS
    );
    return () => clearInterval(timer);
  }, [pinned]);

  /* Auto-advance the mode picker the same way — cycles Find Talent
     → Evaluate My Candidates → End-to-End Hiring so the section
     demonstrates itself instead of sitting on one mode until
     clicked. Reduced motion holds on the default mode instead of
     cycling. */
  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || modePinned) return undefined;
    const timer = setInterval(() => {
      setMode((m) => {
        const i = MODES.findIndex((x) => x.id === m);
        return MODES[(i + 1) % MODES.length].id;
      });
    }, MODE_ROTATE_MS);
    return () => clearInterval(timer);
  }, [modePinned]);

  /* Auto-advance the without/with Ann stepper — cycles Search →
     Alignment → Engagement → Evaluation → The decision so the
     comparison demonstrates itself instead of sitting on one stage
     until clicked. Reduced motion holds on the default stage. */
  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || stagePinned) return undefined;
    const timer = setInterval(() => {
      setStage((s) => {
        const i = WC_STAGES.findIndex((x) => x.id === s);
        return WC_STAGES[(i + 1) % WC_STAGES.length].id;
      });
    }, STAGE_ROTATE_MS);
    return () => clearInterval(timer);
  }, [stagePinned]);

  const activeMode = MODES.find((m) => m.id === mode) || MODES[2];
  const emph = EMPHASIS[active] || [];
  const activeStageIdx = WC_STAGES.findIndex((s) => s.id === stage);
  const activeStage = WC_STAGES[activeStageIdx] || WC_STAGES[0];

  return (
    <div className="home-b">
      {/* ── HERO · the held breath ───────────────────────── */}
      <section className="hb-hero-quote" aria-labelledby="hb-hook">
        <div className="hb-wrap-narrow">
          <p className="line" id="hb-hook">
            &ldquo;I&rsquo;ve got a good feeling about this one.&rdquo;
          </p>
          <p className="sub">
            The sentence that decides more hires &mdash; and more P&amp;Ls &mdash; than
            any spreadsheet in your company.
          </p>
        </div>
      </section>

      {/* ── HERO · the answer ────────────────────────────── */}
      <section className="hb-hero-answer">
        <div className="hb-wrap-narrow inner">
          <h1>
            Success. <span className="accent">Predefined.</span>
          </h1>
          <p className="lede">
            Hiring has always begun with a Job Description. Ann begins by defining
            success for the role &mdash; then searches, engages and evaluates every
            candidate against it.
          </p>
          <Link to="/send-a-role" className="hb-cta hb-cta-light">
            Send Ann a role
          </Link>
          <p className="hb-fineprint">
            Receive a free live project within 48 hours &mdash; the success definition
            and five evaluated candidates for one role.
          </p>
        </div>
      </section>

      {/* ── 01 · The Hiring Intelligence Engine ──────────── */}
      <section className="hb-engine" aria-labelledby="hb-engine-h">
        <div className="hb-wrap">
          <div className="hb-eyebrow on-dark">
            <span className="hb-kick num">01</span>
            <span className="rule" />
            <span className="hb-kick label">The Hiring Intelligence Engine</span>
          </div>

          <div className="hb-engine-top">
            <div className="head">
              <h2 id="hb-engine-h">From role to successful hire.</h2>
              <p>
                You send Ann a role. She returns the people most likely to succeed
                &mdash; and the evidence behind every recommendation.
              </p>
            </div>

            <div className="hb-readout" aria-hidden="true">
              {STAGES.map((s, i) => (
                <span key={s.n} className={`hb-readout-ln st-s${i + 1}`}>
                  {s.readout}
                  <span className="hb-caret">_</span>
                </span>
              ))}
            </div>
          </div>

          <div className="hb-track" aria-hidden="true">
            <div className="hb-track-base" />
            <div className="hb-track-tail" />
            <div className="hb-comet">
              <span className="hb-comet-trail" />
              <span className="hb-comet-dot" />
              <span className="hb-comet-ring" />
            </div>
            {STAGES.map((s, i) => (
              <span
                key={s.n}
                className={`hb-station st-m${i + 1}`}
                style={{ left: `${(i / (STAGES.length - 1)) * 100}%` }}
              >
                <span className={`hb-ping st-p${i + 1}`} />
              </span>
            ))}
          </div>

          <div className="hb-stages">
            {STAGES.map((s, i) => (
              <div key={s.n} className={`hb-stage st-e${i + 1}`}>
                <span className="tag">Stage {s.n}</span>
                <h3>{s.name}</h3>
                <p>{s.body}</p>
                <span className="out">&rarr; {s.tag}</span>
              </div>
            ))}
          </div>

          <div className="hb-engine-result">
            <span className="tag">Out the other end</span>
            <p>A ranked shortlist &mdash; with the evidence behind every name.</p>
          </div>
        </div>
      </section>

      {/* ── 02 · Start where you are ─────────────────────── */}
      <section className="hb-modes" aria-labelledby="hb-modes-h">
        <div className="hb-wrap">
          <div className="hb-eyebrow on-light">
            <span className="hb-kick num">02</span>
            <span className="rule" />
            <span className="hb-kick label">Start where you are</span>
          </div>

          <h2 id="hb-modes-h">
            You don&rsquo;t have to adopt the whole workflow to benefit.
          </h2>

          <div className="hb-chips" role="group" aria-label="Choose how you start">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`hb-chip${mode === m.id ? " is-on" : ""}`}
                aria-pressed={mode === m.id}
                onClick={() => {
                  setMode(m.id);
                  setModePinned(true);
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          <div className="hb-modecard">
            <div className="hb-ministages">
              {STAGES.map((s, i) => (
                <div
                  key={s.n}
                  className={`hb-ministage${
                    activeMode.on.includes(i) ? " is-on" : ""
                  }`}
                >
                  <div className="row">
                    <span className="hb-node" />
                    <span className="n">{s.n}</span>
                  </div>
                  <h3>{s.name}</h3>
                </div>
              ))}
            </div>
            <p className="hb-modedesc">{activeMode.desc}</p>
          </div>

          <p className="hb-modefoot">
            Every mode runs on the same Success Definition &mdash; success defined
            before anyone is judged.
          </p>
        </div>
      </section>

      {/* ── 03 · What changes for your team ──────────────── */}
      <section className="hb-changes" aria-labelledby="hb-changes-h">
        <div className="hb-wrap">
          <div className="hb-eyebrow on-light">
            <span className="hb-kick num">03</span>
            <span className="rule" />
            <span className="hb-kick label">What changes for your team</span>
          </div>

          <h2 id="hb-changes-h">Great recruiters become extraordinary.</h2>
          <p className="lede">
            Ann runs the grind. Recruiters run the relationships.
          </p>

          <div className="hb-stepper">
            <span className="hb-stepper-glow" aria-hidden="true" />
            <div className="hb-stepper-top">
              <div className="hb-stepper-pills" role="tablist" aria-label="Hiring stage">
                {WC_STAGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={s.id === stage}
                    className={`hb-stepper-pill${s.id === stage ? " is-on" : ""}`}
                    onClick={() => {
                      setStage(s.id);
                      setStagePinned(true);
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <span className="hb-stepper-chapter">
                Chapter {String(activeStageIdx + 1).padStart(2, "0")} /{" "}
                {String(WC_STAGES.length).padStart(2, "0")}
              </span>
            </div>

            <div className="hb-stepper-grid">
              <div className="hb-stepper-col x">
                <span className="hb-stepper-lbl x">Without Ann</span>
                <div className="hb-stepper-items" key={`x-${activeStage.id}`}>
                  {activeStage.without.map((t, i) => (
                    <div
                      className="hb-stepper-item"
                      style={{ animationDelay: `${i * 70}ms` }}
                      key={t}
                    >
                      <span className="hb-stepper-mark x" aria-hidden="true">
                        &times;
                      </span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="hb-stepper-col c">
                <span className="hb-stepper-accent" aria-hidden="true" />
                <span className="hb-stepper-lbl c">With Ann</span>
                <div className="hb-stepper-items" key={`c-${activeStage.id}`}>
                  {activeStage.withAnn.map((t, i) => (
                    <div
                      className="hb-stepper-item"
                      style={{ animationDelay: `${i * 70}ms` }}
                      key={t}
                    >
                      <span className="hb-stepper-mark c" aria-hidden="true">
                        &#10003;
                      </span>
                      <span>{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <p className="pullquote">
            Not a better version of the old process. A measured one.
          </p>

          <div className="hb-cards">
            <div className="hb-card">
              <h3>For recruiters</h3>
              <p>
                Ann takes on the repetitive work &mdash; defining success, searching,
                understanding candidates, documenting evidence &mdash; so recruiters
                spend their time where humans win: relationships, advising hiring
                managers, closing exceptional talent.
              </p>
            </div>
            <div className="hb-card">
              <h3>For hiring managers</h3>
              <p>
                Alignment happens upfront: the Success Definition is agreed before
                the search begins &mdash; the hiring manager, the recruiter and every
                interviewer working to one standard. Debriefs compare evidence, not
                opinions, and late-stage surprises disappear.
              </p>
            </div>
            <div className="hb-card">
              <h3>For the business</h3>
              <p>
                Roles filled with decisions you can explain, defend and audit.
              </p>
            </div>
          </div>

          <div className="hb-triad">
            <span className="t">More confidence.</span>
            <span className="t">More conversations.</span>
            <span className="t">More positions closed.</span>
          </div>
        </div>
      </section>

      {/* ── 04 · The evidence layer ──────────────────────── */}
      <section className="hb-evidence" aria-labelledby="hb-evidence-h">
        <div className="hb-wrap">
          <div className="hb-eyebrow on-dark">
            <span className="hb-kick num">04</span>
            <span className="rule" />
            <span className="hb-kick label">The evidence layer</span>
          </div>

          <h2 id="hb-evidence-h">
            Hiring shouldn&rsquo;t ask you to trust a score. It should show you the
            reasoning.
          </h2>

          <div className="hb-ev-grid">
            <div>
              {/* Not aria-live: it rotates on a timer, and announcing
                  every 7s would talk over the reader. The dots below
                  give keyboard/AT users direct access instead. */}
              <div
                className="hb-frame"
                role="group"
                aria-label="Artifacts Ann produces"
              >
                {ARTIFACTS.map((a, i) => (
                  <div
                    key={a.tag + i}
                    className={`hb-slide${i === active ? " is-active" : ""}`}
                    aria-hidden={i !== active}
                  >
                    <div className="scroll">
                      <p className="stage">{a.stage}</p>
                      <p className="tag">{a.tag}</p>
                      <p className="body">{a.body}</p>
                    </div>
                  </div>
                ))}

                <div className="hb-dots">
                  {ARTIFACTS.map((a, i) => (
                    <button
                      key={a.tag + i}
                      type="button"
                      className={`hb-dot${i === active ? " is-active" : ""}`}
                      aria-label={`Show artifact ${i + 1} of ${ARTIFACTS.length}`}
                      aria-current={i === active}
                      onClick={() => {
                        setActive(i);
                        setPinned(true);
                      }}
                    />
                  ))}
                </div>
              </div>
              <p className="hb-framecap">
                One real role, followed end to end. Anonymised.
              </p>
            </div>

            <div className="hb-annos">
              {ANNOS.map((a, i) => (
                <div
                  key={a.label}
                  className={`hb-anno${emph.includes(i) ? " is-hot" : ""}`}
                >
                  <span className="marker" aria-hidden="true" />
                  <div>
                    <p className="label">{a.label}</p>
                    <p className="desc">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 05 · Two audiences ───────────────────────────── */}
      <section className="hb-audiences" aria-label="Two audiences">
        <div className="hb-wrap">
          <div className="hb-eyebrow on-light">
            <span className="hb-kick num">05</span>
            <span className="rule" />
            <span className="hb-kick label">Two audiences</span>
          </div>

          <div className="hb-aud-grid">
            <div className="hb-aud">
              <p className="who">Employers</p>
              <h3>
                Build stronger teams, with one definition of success for
                every hire.
              </h3>
              <Link to="/send-a-role" className="hb-cta hb-cta-light">
                Send Ann a role
              </Link>
            </div>
            <div className="hb-aud">
              <p className="who">Recruitment &amp; Executive Search Firms</p>
              <h3>
                Increase recruiter capacity, quality and placement success.
              </h3>
              <Link to="/for/staffing-firms" className="hb-cta hb-cta-light">
                Ann for recruitment firms &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── 06 · Close ───────────────────────────────────── */}
      <section className="hb-close">
        <div className="hb-wrap-narrow">
          <p className="quote">
            &ldquo;I&rsquo;ve got a good feeling about this one.&rdquo;
          </p>
          <h2>
            Some feelings are right.
            <br />
            Now you&rsquo;ll know which ones.
          </h2>
          <Link to="/send-a-role" className="hb-cta hb-cta-dark">
            Send Ann a role
          </Link>
          <p className="hb-fineprint">
            Receive a free live project within 48 hours.
          </p>
        </div>
      </section>
    </div>
  );
}
